<template>
  <v-card class="pa-6">
    <v-card-title class="text-h5 text-primary mb-4">
      Formulário de Inscrição - Dados Pessoais
    </v-card-title>

    <v-card-text>
      <v-form ref="form" v-model="valid" lazy-validation>

        <!-- Overlay de carregamento -->
        <v-overlay :value="loading" absolute>
          <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
          <span class="ml-4 text-h6">Carregando dados do usuário...</span>
        </v-overlay>

        <!-- Informações Pessoais -->
        <div class="section-header mb-4">Informações Pessoais</div>
        <v-row dense>
          <!-- Nome do candidato (apenas visual) -->
          <v-col cols="12" md="6">
            <div class="field-label">Nome do candidato (*)</div>
            <div class="field-value">{{ candidateName }}</div>
            <input type="hidden" name="full_name" v-model="form.full_name" />
          </v-col>

          <!-- Nome social -->
          <v-col cols="12" md="6">
            <v-text-field v-model="form.social_name" label="Nome social (Opcional)" :disabled="loading"
              @blur="handleSocialNameBlur" :rules="[$rules.fullNameFormat]" hint="Nome Social não é apelido, é a designação (prenome) pela qual pessoas Travestis e Transexuais se identificam"
              persistent-hint />
          </v-col>

          <!-- E-mail principal (apenas visual) -->
          <v-col cols="12" md="6">
            <div class="field-label">E-mail (*)</div>
            <div class="field-value">{{ primaryEmail }}</div>
            <input type="hidden" name="primary_email" v-model="form.primary_email" />
          </v-col>

          <!-- E-mail alternativo -->
          <v-col cols="12" md="6" class="pt-6">
            <v-text-field
              v-model="form.alternative_email"
              :label="isUnicampEmail ? 'E-mail alternativo*' : 'E-mail alternativo (Opcional)'"
              :rules="[validateAlternativeEmail]"
              :disabled="loading"
            />
          </v-col>

          <!-- Aviso sobre alteração de nome e e-mail -->
          <v-col cols="12">
            <small class="grey--text text-caption">
              (*) Para alterar o Nome completo e o E-mail, utilize a opção “Alterar Dados” no seu perfil.
            </small>
          </v-col>

          <!-- País de origem -->
          <v-col cols="12" md="4">
            <v-autocomplete v-model="form.country_of_origin" :items="countries" label="País de origem*"
              :rules="[$rules.required]" required :disabled="loading" />
          </v-col>

          <!-- Data de nascimento -->
          <v-col cols="12" md="4">
            <v-text-field v-model="form.date_of_birth" label="Data de nascimento*" type="date"
              :rules="[validateBirthDate]" required :disabled="loading" />
          </v-col>

          <!-- Raça / Cor Autodeclarada -->
          <v-col cols="12" md="4">
            <v-select
              v-model="form.self_reported_color"
              :items="selfReportedColorOptions"
              item-title="title"
              item-value="value"
              label="Cor Autodeclarada*"
              :rules="[$rules.required]"
              required
              :disabled="loading"
            />
          </v-col>

          <!-- Gênero -->
          <v-col cols="12" md="4">
            <v-select v-model="form.gender" :items="genders" label="Gênero*" :rules="[$rules.required]" required
              :disabled="loading" />
          </v-col>

          <!-- Estado civil -->
          <v-col cols="12" md="4">
            <v-select v-model="form.marital_status" :items="maritalStatusList" label="Estado civil*"
              :rules="[$rules.required]" required :disabled="loading" />
          </v-col>

          <!-- RG / RNE -->
          <v-col v-if="isBrazilian" cols="12" md="4">
            <v-text-field v-model="form.rg" label="RG*" :rules="[validateRGField]" required :disabled="loading" />
          </v-col>

          <v-col v-else cols="12" md="4">
            <v-text-field v-model="form.rne" label="RNE" :rules="[validateRneOrPassport]" :disabled="loading" />
          </v-col>

          <!-- CPF / Passaporte -->
          <v-col v-if="isBrazilian" cols="12" md="4">
            <v-text-field v-model="form.cpf" label="CPF*" :rules="[validateCPFField]" required v-mask-cpf
              :disabled="loading" />
          </v-col>

          <v-col v-else cols="12" md="4">
            <v-text-field v-model="form.passport" label="Passaporte" :rules="[validateRneOrPassport]" :disabled="loading" />
          </v-col>

          <!-- Validade do Documento (Estrangeiros) -->
          <v-col v-if="!isBrazilian" cols="12" md="4">
            <v-text-field v-model="form.document_expiration_date" label="Validade do Documento*" type="date"
              :rules="[validateDocumentExpirationDate]" required :disabled="loading" />
          </v-col>

          <!-- Naturalização (Estrangeiros) -->
          <v-col v-if="!isBrazilian" cols="12" md="4">
            <v-select
              v-model="form.naturalization"
              :items="naturalizationOptions"
              item-title="title"
              item-value="value"
              label="Possui Naturalização?*"
              :rules="[validateNaturalization]"
              required
              :disabled="loading"
            />
          </v-col>

          <!-- Telefone -->
          <v-col cols="12" md="4">
            <v-text-field v-model="form.phone" label="(DDD) Telefone*" :rules="[$rules.required]" required
              @input="formatPhone('phone')" maxlength="15" :disabled="loading" />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.alternative_phone" label="(DDD) Telefone (Opcional)"
              @input="formatPhone('alternative_phone')" maxlength="15" :disabled="loading" />
          </v-col>

          <!-- Nome da mãe -->
          <v-col cols="12" md="6">
            <v-text-field v-model="form.mother_name" label="Nome completo da mãe*" :rules="[$rules.required, $rules.fullNameFormat]" required
              :disabled="loading" />
          </v-col>

          <!-- Necessidades Especiais -->
          <v-col cols="12" md="6">
            <v-select v-model="form.special_needs" :items="specialNeedsOptions" label="Necessidades Especiais*"
              :rules="[$rules.required]" required :disabled="loading" />
          </v-col>
        </v-row>

        <!-- Endereço -->
        <div class="section-header mb-4">Endereço</div>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-autocomplete v-model="form.country" :items="countries" label="País*" :rules="[$rules.required]" required
              :disabled="loading" />
          </v-col>

          <v-col v-if="isAddressBrazilian" cols="12" md="2">
            <v-text-field v-model="form.zip_code" label="CEP*" :rules="[$rules.required, v => !v || v.length <= 9 || 'CEP inválido']" v-mask="'#####-###'" maxlength="9"
              @update:modelValue="getTotalAddressByCEP" :disabled="loading" />
          </v-col>

          <v-col cols="12" :md="isAddressBrazilian ? 2 : 3">
            <v-autocomplete
              v-model="form.street_type"
              :items="streetTypes"
              label="Tipo de logradouro*"
              :rules="[$rules.required]"
              required
              :disabled="loading"
            />
          </v-col>

          <v-col cols="12" :md="isAddressBrazilian ? 3 : 4">
            <v-text-field v-model="form.street" label="Rua / Logradouro*" :rules="[$rules.required]" required
              :disabled="loading" />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field v-model="form.street_number" label="Número*" :rules="[$rules.required]" required
              :disabled="loading" />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field v-model="form.district"
              :label="isAddressBrazilian ? 'Bairro*' : 'Bairro / Distrito (Opcional)'"
              :rules="isAddressBrazilian ? [$rules.required] : []" :required="isAddressBrazilian" :disabled="loading" />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field v-model="form.city" label="Cidade*" :rules="[$rules.required]" required :disabled="loading" />
          </v-col>

          <v-col cols="12" md="4">
            <v-select v-if="isAddressBrazilian" v-model="form.state" :items="brazilStates" label="Estado*"
              :rules="[$rules.required]" required :disabled="loading" />
            <v-text-field v-else v-model="form.state" label="Região / Estado / Província*" :rules="[$rules.required]"
              required :disabled="loading" />
          </v-col>

          <v-col cols="12">
            <v-text-field v-model="form.address_complement" label="Complemento (Opcional)" :disabled="loading" />
          </v-col>
        </v-row>

        <!-- Botões -->
        <v-row justify="end" class="mt-4" dense>
          <v-col cols="auto">
            <v-btn color="secondary" large @click="saveProfile(false)" :disabled="loading || submitting">
              Salvar
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" large @click="saveProfile(true)" :disabled="loading || submitting">
              Salvar e Preencher Inscrição
            </v-btn>
          </v-col>
        </v-row>

        <v-dialog v-model="socialNameDialog" max-width="640">
          <v-card>
            <v-card-title class="text-h6 text-primary">
              Confirmar uso do nome social
            </v-card-title>

            <v-card-text>
              <p class="mb-3">
                O nome social será utilizado em seu lugar em todas as etapas do processo seletivo — listas de aprovados,
                comunicações e documentos internos. Seu nome de documento permanecerá registrado no sistema, mas não
                será exibido publicamente.
              </p>
              <p class="mb-3">
                1 - Nome Social não é apelido, é a designação (prenome) pela qual pessoas Travestis e Transexuais se
                identificam, são reconhecidas e identificadas em suas relações sociais.
              </p>
              <p class="mb-3">
                2 - Ao confirmar, o Nome Social será registrado e exibido no lugar do nome de documento em todo o
                processo seletivo.
              </p>
              <p class="mb-0">
                Resolução GR-005/2020:
                <a class="social-name-link" href="https://www.pg.unicamp.br/norma/17662/0" target="_blank"
                  rel="noopener noreferrer">https://www.pg.unicamp.br/norma/17662/0</a>.
              </p>
            </v-card-text>

            <v-card-actions class="justify-end">
              <v-btn variant="text" @click="cancelSocialNameConfirmation">
                Cancelar
              </v-btn>
              <v-btn color="primary" variant="flat" @click="confirmSocialNameConfirmation">
                Confirmar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios";
import keycloak from '../../plugins/keycloak';
import { getCountries } from '@/services/countriesService';
import { getAddressByCep } from '@/services/cepService';

export default {
  name: 'ApplicationFormPersonal',
  props: { modelValue: Object },
  emits: ['update:modelValue', 'next'],
  data() {
    return {
      loading: true,
      submitting: false,
      legalFullName: '',
      socialNameDialog: false,
      pendingSocialName: '',
      lastConfirmedSocialName: '',
      form: {
        full_name: '',
        primary_email: '',
        kc_uuid: '',
        social_name: '',
        country_of_origin: '',
        date_of_birth: '',
        self_reported_color: null,
        document_expiration_date: null,
        naturalization: null,
        gender: '',
        marital_status: '',
        rg: '',
        rne: '',
        cpf: '',
        passport: '',
        alternative_email: '',
        phone: '',
        alternative_phone: '',
        mother_name: '',
        special_needs: '',
        country: '',
        zip_code: '',
        street_type: '',
        street: '',
        street_number: '',
        district: '',
        city: '',
        state: '',
        address_complement: '',
        id: null,
        ...this.modelValue,
      },
      processReminderForm: {
        user_id: '',
        process_id: '',
      },
      selfReportedColorOptions: [
        { title: 'Não Declarada', value: 'NAO_DECLARADA' },
        { title: 'Amarela', value: 'AMARELA' },
        { title: 'Branca', value: 'BRANCA' },
        { title: 'Indígena', value: 'INDIGENA' },
        { title: 'Preta', value: 'PRETA' },
        { title: 'Parda', value: 'PARDA' },
      ],
      naturalizationOptions: [
        { title: 'Sim', value: 'S' },
        { title: 'Não', value: 'N' },
      ],
      specialNeedsOptions: [
        'Sem necessidades especiais',
        'Deficiência motora',
        'Deficiência auditiva',
        'Deficiência visual',
        'Deficiência visual parcial',
        'Deficiência auditiva parcial',
        'Deficiência física',
        'Deficiência visual e auditiva',
        'Deficiência múltipla',
        'Deficiência mental',
      ],
      streetTypes: [
        'AEROPORTO', 'ALAMEDA', 'AREA', 'AVENIDA', 'CAMPO', 'CHACARA',
        'COLONIA', 'CONDOMINIO', 'CONJUNTO', 'DISTRITO', 'ESPLANADA',
        'ESTACAO', 'ESTRADA', 'FAVELA', 'FAZENDA', 'FEIRA', 'JARDIM',
        'LADEIRA', 'LAGO', 'LAGOA', 'LARGO', 'LOTEAMENTO', 'MORRO',
        'NUCLEO', 'OUTROS', 'PARQUE', 'PASSARELA', 'PATIO', 'PRACA',
        'QUADRA', 'RECANTO', 'RESIDENCIAL', 'RODOVIA', 'RUA', 'SETOR',
        'SITIO', 'TRAVESSA', 'TRECHO', 'VALE', 'VEREDA', 'VIA',
        'VIADUTO', 'VIELA', 'VILA',
      ],
      valid: false,
      genders: ['Masculino', 'Feminino'],
      maritalStatusList: ['Solteiro', 'Casado', 'Divorciado', 'Viúvo'],
      countries: [],
      brazilStates: [
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
        'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
        'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
      ],
    };
  },
  computed: {
    isBrazilian() { return String(this.form.country_of_origin || '').toLowerCase() === 'brasil'; },
    isAddressBrazilian() { return String(this.form.country || '').toLowerCase() === 'brasil'; },
    fullName() { return `${keycloak.idTokenParsed?.given_name || ''} ${keycloak.idTokenParsed?.family_name || ''}`.trim(); },
    normalizedSocialName() { return this.normalizeSocialName(this.form.social_name); },
    candidateName() { return this.form.full_name || this.legalFullName || this.fullName; },
    primaryEmail() { return keycloak.idTokenParsed?.email || ''; },
    isUnicampEmail() {
      const email = String(this.form.primary_email || this.primaryEmail || '').toLowerCase();
      const domain = email.split('@')[1] || '';
      return domain.includes('unicamp');
    },
  },
  async mounted() {
    this.form.kc_uuid = keycloak.idTokenParsed?.sub || '';
    try {
      await this.loadCountries();
      await this.loadUserProfile();
      this.form.social_name = this.normalizeSocialName(this.form.social_name);
      this.lastConfirmedSocialName = this.form.social_name;
    } finally { this.loading = false; }
    this.legalFullName = this.fullName;
    this.form.full_name = this.legalFullName;
    this.form.primary_email = this.primaryEmail;
    window.history.replaceState({}, document.title, window.location.pathname);
  },
  methods: {
    removeAccents(str) {
      return String(str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();
    },
    parseStreet(fullStreet) {
      if (!fullStreet) return { streetType: '', streetName: '' };
      const trimmed = String(fullStreet).trim();
      const parts = trimmed.split(/\s+/);
      const firstWordNormalized = this.removeAccents(parts[0]);

      if (this.streetTypes.includes(firstWordNormalized)) {
        return {
          streetType: firstWordNormalized,
          streetName: parts.slice(1).join(' ')
        };
      }

      return {
        streetType: '',
        streetName: trimmed
      };
    },
    normalizeSocialName(value) {
      return String(value || '').trim().replace(/\s+/g, ' ');
    },
    async loadCountries() {
      this.countries = await getCountries();
      this.form.country_of_origin ||= 'Brasil';
      this.form.country ||= 'Brasil';
    },
    async loadUserProfile() {
      const kcUuid = keycloak.idTokenParsed?.sub;
      try {
        const response = await this.$axios.get(`${this.$PROFPATH}/profiles`,
        {
          params: {
            filters: {
              kc_uuid: kcUuid,
            },
          },
        });
        const profileData = response.data?.data?.[0];
        if (profileData) {
          Object.assign(this.form, profileData, { id: profileData.id });
          this.form.full_name = this.form.full_name || this.legalFullName;

          if (!this.form.street_type && this.form.street) {
            const { streetType, streetName } = this.parseStreet(this.form.street);
            if (streetType) {
              this.form.street_type = streetType;
              this.form.street = streetName;
            }
          }
        }
      } catch (err) { console.error(err); }
    },
    handleSocialNameBlur() {
      const normalizedValue = this.normalizeSocialName(this.form.social_name);

      if (!normalizedValue) {
        this.form.social_name = '';
        this.pendingSocialName = '';
        this.lastConfirmedSocialName = '';
        return;
      }

      this.form.social_name = normalizedValue;

      if (normalizedValue === this.lastConfirmedSocialName) return;

      this.pendingSocialName = normalizedValue;
      this.socialNameDialog = true;
    },
    confirmSocialNameConfirmation() {
      this.form.social_name = this.pendingSocialName;
      this.lastConfirmedSocialName = this.pendingSocialName;
      this.pendingSocialName = '';
      this.socialNameDialog = false;
    },
    cancelSocialNameConfirmation() {
      this.form.social_name = '';
      this.pendingSocialName = '';
      this.lastConfirmedSocialName = '';
      this.socialNameDialog = false;
    },
    formatPhone(field) {
      if (!this.form[field]) return;
      let val = this.form[field].replace(/\D/g, '');
      if (val.length > 11) val = val.slice(0, 11);
      if (val.length > 2 && val.length <= 6) val = `(${val.slice(0, 2)}) ${val.slice(2)}`;
      else if (val.length > 6 && val.length <= 10) val = `(${val.slice(0, 2)}) ${val.slice(2, val.length - 4)}-${val.slice(-4)}`;
      else if (val.length > 10) val = `(${val.slice(0, 2)}) ${val.slice(2, 7)}-${val.slice(7)}`;
      this.form[field] = val;
    },
    validateAlternativeEmail(value) {
      if (this.isUnicampEmail && !value) {
        return 'E-mail alternativo é obrigatório para e-mails do domínio Unicamp.';
      }
      if (value && this.$rules?.email) {
        return this.$rules.email(value);
      }
      return true;
    },
    async validateCPFField(value) {
      if (!value) return 'CPF é obrigatório.';
      if (!this.validateCPF(value)) {
        return 'CPF inválido.';
      }
      const exists = await this.checkCpfExists(value);
      if (exists) {
        return 'Este CPF já possui uma inscrição cadastrada.';
      }
      return true;
    },
    async checkCpfExists(cpf) {
      const process_id = this.$store.state.processId ? this.$store.state.processId : localStorage.getItem('processId');
      const process_type = this.$store.state.type ? this.$store.state.type : localStorage.getItem('type');
      try {
        const endpoint = process_type === "special" ? "special-applications" : "regular-applications";
        const { data } = await axios.post(`${this.$BACKPATH}/${endpoint}/check-cpf`, {
          "cpf": cpf,
          "process_id": process_id,
        },
          {
            headers: { Authorization: `Bearer ${keycloak.token}` },
          }
        );
        const responseData = data.data;
        if (responseData.has_duplicate) {
          const currentUserId = String(this.form.id);
          const existingIds = responseData.existing_user_ids.map(id => String(id));
          const isOtherProfileDuplicate = existingIds.some(id => id !== currentUserId);
          if (isOtherProfileDuplicate) {
            this.$toast.error('Já existe outra conta cadastrada com este CPF inscrita neste processo.');
          }
        }
      } catch (error) {
        console.error('Erro ao verificar existência do CPF:', error);
        return false;
      }
    },
    validateCPF(cpf) {
      cpf = cpf.replace(/[^\d]+/g, '');
      if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
      let soma = 0; for (let i = 0; i < 9; i++) soma += parseInt(cpf[i]) * (10 - i);
      let resto = (soma * 10) % 11; if (resto === 10 || resto === 11) resto = 0;
      if (resto !== parseInt(cpf[9])) return false;
      soma = 0; for (let i = 0; i < 10; i++) soma += parseInt(cpf[i]) * (11 - i);
      resto = (soma * 10) % 11; if (resto === 10 || resto === 11) resto = 0;
      return resto === parseInt(cpf[10]);
    },
    validateRGField(value) { if (!value) return 'RG é obrigatório.'; return this.validateRG(value) ? true : 'RG inválido.'; },
    validateRG(rg) {
      const regex = /^[A-Za-z0-9.-]{5,20}$/;
      return regex.test(rg);
    },
    validateRneOrPassport() {
      if (this.isBrazilian) {
        return true;
      }
      if (!this.form.rne && !this.form.passport) {
        return 'Informe o RNE ou o Passaporte.';
      }
      return true;
    },
    validateBirthDate(value) {
      if (!value) return 'Data de nascimento é obrigatória.';
      const parts = value.split('-');
      if (parts.length !== 3) return 'Data de nascimento inválida.';
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      if (year < 1920) {
        return 'O ano de nascimento não pode ser inferior a 1920.';
      }
      const birthDate = new Date(year, month, day);
      const today = new Date();
      if (birthDate > today) {
        return 'Data de nascimento não pode estar no futuro.';
      }
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 15) {
        return 'O candidato deve ter no mínimo 15 anos de idade.';
      }
      return true;
    },
    validateDocumentExpirationDate(value) {
      if (this.isBrazilian) return true;
      if (!value) return 'Data de validade do documento é obrigatória.';
      const parts = value.split('-');
      if (parts.length !== 3) return 'Data inválida.';
      const expDate = new Date(parts[0], parts[1] - 1, parts[2]);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (expDate <= today) {
        return 'A data de validade deve ser maior que a data atual.';
      }
      return true;
    },
    validateNaturalization(value) {
      if (this.isBrazilian) return true;
      if (!value) return 'Informe se possui naturalização.';
      return true;
    },
    validateFormFields() {
      const f = this.form;
      if (!f.full_name) { this.$toast.error('Nome completo é obrigatório.'); return false; }
      if (!f.primary_email) { this.$toast.error('E-mail principal é obrigatório.'); return false; }
      if (this.isUnicampEmail && !f.alternative_email) { this.$toast.error('E-mail alternativo é obrigatório para e-mails do domínio Unicamp.'); return false; }
      if (!f.country_of_origin) { this.$toast.error('País de origem é obrigatório.'); return false; }
      if (!f.date_of_birth || this.validateBirthDate(f.date_of_birth) !== true) { this.$toast.error(this.validateBirthDate(f.date_of_birth)); return false; }
      if (!f.self_reported_color) { this.$toast.error('Cor / Raça autodeclarada é obrigatória.'); return false; }
      if (!this.isBrazilian) {
        const docExpValid = this.validateDocumentExpirationDate(f.document_expiration_date);
        if (docExpValid !== true) { 
          this.$toast.error(docExpValid); 
          return false; 
        }
        if (!f.naturalization) { 
          this.$toast.error('Campo Naturalização é obrigatório.'); 
          return false; 
        }
      }
      if (!f.gender) { this.$toast.error('Gênero é obrigatório.'); return false; }
      if (!f.marital_status) { this.$toast.error('Estado civil é obrigatório.'); return false; }
      if (this.isBrazilian && (!f.rg || !this.validateRG(f.rg))) { this.$toast.error('RG inválido ou ausente.'); return false; }
      if (this.isBrazilian && (!f.cpf || !this.validateCPF(f.cpf))) { this.$toast.error('CPF inválido ou ausente.'); return false; }
      if (!this.isBrazilian && !f.rne && !f.passport) { this.$toast.error('Obrigatório informar Passaporte ou RNE.'); return false; }
      if (!f.phone) { this.$toast.error('Telefone é obrigatório.'); return false; }
      if (!f.mother_name) { this.$toast.error('Nome da mãe é obrigatório.'); return false; }
      if (!f.special_needs) { this.$toast.error('Necessidades Especiais é obrigatório.'); return false; }
      if (!f.country) { this.$toast.error('País do endereço é obrigatório.'); return false; }
      if (this.isAddressBrazilian && !f.zip_code) { this.$toast.error('CEP é obrigatório.'); return false; }
      if (!f.street_type) { this.$toast.error('Tipo de logradouro é obrigatório.'); return false; }
      if (!f.street) { this.$toast.error('Rua / Logradouro é obrigatório.'); return false; }
      if (!f.street_number) { this.$toast.error('Número é obrigatório.'); return false; }
      if (this.isAddressBrazilian && !f.district) { this.$toast.error('Bairro é obrigatório.'); return false; }
      if (!f.city) { this.$toast.error('Cidade é obrigatória.'); return false; }
      if (!f.state) { this.$toast.error('Estado / Região é obrigatório.'); return false; }
      return true;
    },
    async saveProfile(shouldNext) {
      const valid = await this.$refs.form.validate();
      if (!valid) return;
      if (!this.validateFormFields()) return;
      this.submitting = true;
      let errorStr;
      try {
        let response;
        this.processReminderForm.process_id = this.$store.state.processId ? this.$store.state.processId : localStorage.getItem('processId');
        if (this.form.id) response = await this.$axios.put(`${this.$PROFPATH}/profiles/${this.form.id}`, this.form);
        else {
          response = await this.$axios.post(`${this.$PROFPATH}/profiles`, this.form);
        }
        if (response.status !== 200 && response.status !== 201) {
          errorStr = JSON.stringify(response.data.errors);
          throw new Error('Erro na API');
        }
        const rData = response.data?.data;
        Object.assign(this.form, rData);
        if (!this.form.id && rData.id) {
          this.form.id = rData.id;
        }
        this.processReminderForm.user_id = this.form.id;
        try {
          response = await this.$axios.post(`${this.$BACKPATH}/process-reminders`, this.processReminderForm);
        } catch (error) {
          console.log('reminder existe');                  
        }
        this.$emit('update:modelValue', this.form);
        this.$toast.success('Dados Pessoais salvos com sucesso!');
        if (shouldNext) this.$emit('next');
      } catch (err) { console.error(err); this.$toast.error(`Falha ao salvar Dados Pessoais. ${errorStr}`); }
      finally { this.submitting = false; }
    },
    async getTotalAddressByCEP(cep) {
      if (!this.isAddressBrazilian || !cep) return;
      const data = await getAddressByCep(cep);
      if (data) {
        if (data.logradouro) {
          const { streetType, streetName } = this.parseStreet(data.logradouro);
          this.form.street_type = streetType || '';
          this.form.street = streetName || data.logradouro;
        }
        this.form.district = data.bairro || this.form.district;
        this.form.city = data.localidade || this.form.city;
        this.form.state = data.uf || this.form.state;
        this.form.country ||= 'Brasil';
      }
    }
  }
};
</script>

<style scoped>
.section-header {
  background-color: #E3F2FD;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  font-weight: bold;
  color: #1976D2;
}

.v-card {
  border-radius: 12px;
}

.field-label {
  font-weight: 500;
  font-size: 1rem;
  color: #1976D2;
  margin-bottom: 4px;
}

.field-value {
  font-size: 1.15rem;
  margin-bottom: 8px;
}

.social-name-link,
.social-name-link:visited,
.social-name-link:hover,
.social-name-link:active,
.social-name-link:focus {
  color: #1976D2 !important;
}
</style>